import { useEffect, useState, useRef } from 'react';
import { EarningsSummaryCard } from '@/components/dashboard/EarningsSummaryCard';
import { DashboardEarningsChart } from '@/components/dashboard/DashboardEarningsChart';
import { RosterList } from '@/components/dashboard/RosterList';
import { useAccountStore } from '@/store/account-store';
import { syncUserInstallEvents } from '@/lib/ga4';
import { getFirebaseServices } from '@/lib/firebase/client';
import { doc, updateDoc, collection, getDocs } from 'firebase/firestore';

export default function DashboardPage() {
  const accounts = useAccountStore((s) => s.accounts);
  const [syncing, setSyncing] = useState(false);
  const hasSynced = useRef(false);

  useEffect(() => {
    if (!accounts || accounts.length === 0 || syncing || hasSynced.current) return;

    const syncAll = async () => {
      setSyncing(true);
      hasSynced.current = true;
      try {
        for (const acc of accounts) {
          if (!acc?.handle) continue;
          try {
            await syncUserInstallEvents(acc.handle);
            console.log(`✅ GA4 sync completed for ${acc.handle}`);
          } catch (e) {
            console.error(`GA4 sync error for ${acc.handle}:`, e);
          }
        }
        
        // After syncing, calculate total earnings from all install events
        const services = getFirebaseServices();
        if (services?.auth?.currentUser && services?.db) {
          try {
            const uid = services.auth.currentUser.uid;
            const eventsRef = collection(services.db, `users/${uid}/installEvents`);
            const eventsSnap = await getDocs(eventsRef);
            
            let totalEarnings = 0;
            eventsSnap.forEach((doc) => {
              const data = doc.data();
              const cpi = data.cpi || 0;
              const count = data.eventCount || 0;
              totalEarnings += cpi * count;
            });
            
            // Update user document with calculated total
            const userRef = doc(services.db, 'users', uid);
            await updateDoc(userRef, { totalEarned: totalEarnings });
            console.log(`✅ Updated totalEarned: $${totalEarnings.toFixed(2)}`);
          } catch (e) {
            console.error('Failed to update totalEarned:', e);
          }
        }
      } finally {
        setSyncing(false);
      }
    };

    syncAll();
  }, [accounts]);

  return (
    <section className="space-y-5 pb-24 lg:pb-8">
      <header>
        <p className="text-sm uppercase tracking-wide text-lime-300">Performance</p>
        <h1 className="text-2xl font-semibold text-white lg:text-3xl">Earnings & roster</h1>
        <p className="text-sm text-white/60">
          Track your active campaigns and earnings from install events.
        </p>
      </header>

      <div className="lg:grid lg:grid-cols-3 lg:gap-6">
        <div className="space-y-5 lg:col-span-1">
          <EarningsSummaryCard />
          <DashboardEarningsChart />
        </div>
        
        <div className="mt-5 lg:col-span-2 lg:mt-0">
          <h2 className="mb-3 text-lg font-semibold text-white lg:hidden">Active games</h2>
          <RosterList />
        </div>
      </div>
    </section>
  );
}
